'use client';

import { Icon } from '@iconify/react';
import { getCalApi } from '@calcom/embed-react';
import gsap from 'gsap';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SocialIcons } from '@/app/components/ui/social-icons';
import { AvatarStack } from './avatar-stack';

export function ContactForm() {
  const { t } = useTranslation();
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    submitted: false,
    loading: false,
    error: null as string | null,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async function initCal() {
      const cal = await getCalApi({ namespace: '30min' });
      cal('ui', { hideEventTypeDetails: false, layout: 'month_view' });
    })();

    if (!containerRef.current) {
      return;
    }

    const items = containerRef.current.querySelectorAll('.gsap-item');
    gsap.fromTo(
      items,
      { opacity: 0, y: 24 },
      {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.08,
        ease: 'power2.out',
      }
    );
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formState.name,
          email: formState.email,
          subject: formState.subject,
          message: formState.message,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to send message');
      }

      setFormState((prev) => ({
        ...prev,
        submitted: true,
        loading: false,
        name: '',
        email: '',
        subject: '',
        message: '',
      }));
    } catch (error) {
      setFormState((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred',
      }));
    }
  };

  return (
    <article ref={containerRef} className="relative flex w-full flex-col gap-10 pb-4 pt-2">
      <section className="gsap-item relative overflow-hidden rounded-[2rem] border border-black/5 bg-[linear-gradient(135deg,rgba(255,255,255,0.94),rgba(239,242,238,0.88))] px-5 py-6 shadow-[0_24px_90px_rgba(24,26,29,0.08)] duration-700 dark:border-white/10 dark:bg-[linear-gradient(135deg,rgba(16,23,29,0.96),rgba(10,16,20,0.96))] dark:shadow-[0_24px_90px_rgba(0,0,0,0.4)] sm:px-7 sm:py-7 md:px-8 md:py-8 xl:rounded-[2.25rem] xl:px-10 xl:py-10">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(circle_at_top,rgba(80,108,131,0.16),transparent_65%)] dark:bg-[radial-gradient(circle_at_top,rgba(80,108,131,0.2),transparent_65%)]" />
        <div className="relative grid gap-8 lg:grid-cols-[minmax(0,0.96fr)_minmax(0,1.04fr)] lg:items-start lg:gap-8 xl:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] xl:gap-10">
          <div className="space-y-7 xl:space-y-8">
            <div className="gsap-item inline-flex w-fit items-center gap-2 rounded-full border border-[#181A1D]/10 bg-white/70 px-4 py-2 text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-[#506C83] shadow-sm dark:border-white/10 dark:bg-white/5 dark:text-[#c7d5e0]">
              {t('Contact.badge')}
            </div>

            <div className="gsap-item max-w-xl space-y-4 sm:space-y-5">
              <h1 className="max-w-2xl text-[2.9rem] font-semibold leading-[0.92] tracking-[-0.06em] text-[#181A1D] dark:text-[#f5f7fa] sm:text-[3.7rem] lg:text-[4.35rem] xl:text-[5rem]">
                <Trans
                  i18nKey="Contact.heading"
                  components={{
                    highlight: <span className="text-[#506C83]" />,
                  }}
                />
              </h1>
              <p className="max-w-lg text-base font-medium leading-relaxed text-[#2b3740] dark:text-[#d7e0e7] sm:text-lg">
                {t('Contact.subheading')}
              </p>
              <p className="max-w-xl text-[0.96rem] leading-7 text-[#4e5b64] dark:text-[#aab7c2] sm:text-base sm:leading-8">
                {t('Contact.description')}
              </p>
            </div>

            <div className="gsap-item flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <button
                data-cal-namespace="30min"
                data-cal-link="ellis-caicedo-hfdo7f/30min"
                data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}'
                className="group inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full border border-[#181A1D] bg-[#181A1D] px-5 py-3 text-[0.82rem] font-semibold tracking-[0.08em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#24313a] hover:shadow-[0_14px_30px_rgba(24,26,29,0.14)] dark:border-[#f5f7fa] dark:bg-[#f5f7fa] dark:text-[#181A1D] dark:hover:bg-[#dfe6eb] sm:min-h-[3.1rem] sm:w-auto"
              >
                {t('Contact.cta')}
                <Icon
                  icon="tabler:arrow-up-right"
                  className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </button>
              <Link
                href="/projects"
                className="inline-flex min-h-12 w-full items-center justify-center rounded-full border border-[#181A1D]/12 bg-white/62 px-5 py-3 text-[0.82rem] font-semibold tracking-[0.04em] text-[#181A1D] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#506C83] hover:bg-white hover:text-[#506C83] hover:shadow-[0_14px_30px_rgba(24,26,29,0.08)] dark:border-white/10 dark:bg-white/5 dark:text-[#f5f7fa] dark:hover:border-[#8ca3b5] dark:hover:bg-white/8 dark:hover:text-[#8ca3b5] sm:min-h-[3.1rem] sm:w-auto"
              >
                {t('Contact.viewWork')}
              </Link>
            </div>

            <div className="gsap-item border-t border-[#181A1D]/10 pt-6 text-sm text-[#4e5b64] dark:border-white/10 dark:text-[#aab7c2]">
              <div className="rounded-[1.45rem] border border-[#181A1D]/8 bg-white/58 px-4 py-4 shadow-[0_14px_36px_rgba(24,26,29,0.05)] dark:border-white/10 dark:bg-white/5 sm:px-5 sm:py-5">
                <AvatarStack />
                <div className="mt-4 border-t border-[#181A1D]/8 pt-4 dark:border-white/10">
                  <p className="max-w-none leading-7 text-[#4e5b64] dark:text-[#aab7c2]">
                    {t('Contact.responseDescription')}
                  </p>
                </div>
                <div className="mt-4 flex flex-wrap gap-2.5 text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-[#506C83] dark:text-[#c7d5e0] sm:text-[0.72rem]">
                  <span className="rounded-full border border-[#506C83]/15 bg-[#506C83]/8 px-3 py-1.5 dark:border-white/10 dark:bg-white/5">
                    {t('Contact.availability.0')}
                  </span>
                  <span className="rounded-full border border-[#506C83]/15 bg-[#506C83]/8 px-3 py-1.5 dark:border-white/10 dark:bg-white/5">
                    {t('Contact.availability.1')}
                  </span>
                  <span className="rounded-full border border-[#506C83]/15 bg-[#506C83]/8 px-3 py-1.5 dark:border-white/10 dark:bg-white/5">
                    {t('Contact.availability.2')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="gsap-item relative mt-8 lg:mt-0">
            <div className="absolute inset-x-8 top-6 h-full rounded-[2rem] bg-[#506C83]/8 blur-3xl dark:bg-[#506C83]/12" />
            <div className="relative overflow-hidden rounded-[1.75rem] border border-black/5 bg-white/88 p-5 shadow-[0_24px_90px_rgba(24,26,29,0.08)] backdrop-blur-xl dark:border-white/10 dark:bg-[#11191f]/90 dark:shadow-[0_24px_90px_rgba(0,0,0,0.45)] sm:p-7 md:p-8 xl:rounded-[2rem] xl:p-10">
              <div className="mb-8 flex items-start justify-between gap-4">
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.26em] text-[#506C83] dark:text-[#c7d5e0]">
                    {t('Contact.formBadge')}
                  </p>
                  <h2 className="text-2xl font-semibold tracking-[-0.04em] text-[#181A1D] dark:text-[#f5f7fa]">
                    {t('Contact.formTitle')}
                  </h2>
                  <p className="max-w-md text-sm leading-7 text-[#55636d] dark:text-[#9fb0bd]">
                    {t('Contact.formDescription')}
                  </p>
                </div>
                <div className="hidden h-fit rounded-full border border-[#181A1D]/10 px-3 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-[#506C83] dark:border-white/10 dark:text-[#c7d5e0] sm:inline-flex sm:items-center sm:justify-center">
                  {t('Contact.replyBadge')}
                </div>
              </div>

              {formState.submitted ? (
                <div className="animate-in fade-in-0 slide-in-from-bottom-3 flex min-h-[24rem] flex-col items-center justify-center text-center duration-500">
                  <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[#e6f4ea] dark:bg-[#173221]">
                    <Icon icon="tabler:check" className="h-10 w-10 text-[#1f7a45] dark:text-[#6ed38f]" />
                  </div>
                  <h3 className="mb-3 text-2xl font-semibold tracking-[-0.04em] text-[#181A1D] dark:text-[#f5f7fa]">
                    {t('Contact.success.title')}
                  </h3>
                  <p className="mb-8 max-w-md text-sm leading-7 text-[#55636d] dark:text-[#9fb0bd]">
                    {t('Contact.success.message')}
                  </p>
                  <button
                    onClick={() => setFormState((prev) => ({ ...prev, submitted: false }))}
                    className="inline-flex min-h-12 items-center justify-center rounded-full bg-[#181A1D] px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#24313a] dark:bg-[#f5f7fa] dark:text-[#181A1D] dark:hover:bg-[#dfe6eb]"
                  >
                    {t('Contact.success.sendAnother')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid gap-5 md:grid-cols-2">
                    <label className="space-y-2">
                      <span className="text-sm font-medium text-[#33414a] dark:text-[#dfe6eb]">
                        {t('Contact.fields.name')}
                      </span>
                      <input
                        type="text"
                        required
                        placeholder={t('Contact.fields.namePlaceholder')}
                        value={formState.name}
                        onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
                        className="min-h-14 w-full rounded-[1.25rem] border border-[#181A1D]/10 bg-[#f8f8f5] px-5 py-4 text-sm text-[#181A1D] outline-none transition-all duration-300 placeholder:text-[#8b98a2] focus:border-[#506C83] focus:bg-white dark:border-white/10 dark:bg-[#0d1419] dark:text-[#f5f7fa] dark:placeholder:text-[#6f808c] dark:focus:border-[#8ca3b5] dark:focus:bg-[#11191f]"
                      />
                    </label>
                    <label className="space-y-2">
                      <span className="text-sm font-medium text-[#33414a] dark:text-[#dfe6eb]">
                        {t('Contact.fields.email')}
                      </span>
                      <input
                        type="email"
                        required
                        placeholder={t('Contact.fields.emailPlaceholder')}
                        value={formState.email}
                        onChange={(e) => setFormState((prev) => ({ ...prev, email: e.target.value }))}
                        className="min-h-14 w-full rounded-[1.25rem] border border-[#181A1D]/10 bg-[#f8f8f5] px-5 py-4 text-sm text-[#181A1D] outline-none transition-all duration-300 placeholder:text-[#8b98a2] focus:border-[#506C83] focus:bg-white dark:border-white/10 dark:bg-[#0d1419] dark:text-[#f5f7fa] dark:placeholder:text-[#6f808c] dark:focus:border-[#8ca3b5] dark:focus:bg-[#11191f]"
                      />
                    </label>
                  </div>

                  <label className="space-y-2">
                    <span className="text-sm font-medium text-[#33414a] dark:text-[#dfe6eb]">
                      {t('Contact.fields.subject')}
                    </span>
                    <select
                      required
                      value={formState.subject}
                      onChange={(e) => setFormState((prev) => ({ ...prev, subject: e.target.value }))}
                      className="min-h-14 w-full rounded-[1.25rem] border border-[#181A1D]/10 bg-[#f8f8f5] px-5 py-4 text-sm text-[#4d5a63] outline-none transition-all duration-300 focus:border-[#506C83] focus:bg-white dark:border-white/10 dark:bg-[#0d1419] dark:text-[#d7e0e7] dark:focus:border-[#8ca3b5] dark:focus:bg-[#11191f]"
                    >
                      <option value="" disabled>
                        {t('Contact.fields.subjectPlaceholder')}
                      </option>
                      <option value="project">{t('Contact.fields.subjectOptions.project')}</option>
                      <option value="consultancy">{t('Contact.fields.subjectOptions.consultancy')}</option>
                      <option value="hiring">{t('Contact.fields.subjectOptions.hiring')}</option>
                      <option value="other">{t('Contact.fields.subjectOptions.other')}</option>
                    </select>
                  </label>

                  <label className="space-y-2">
                    <span className="text-sm font-medium text-[#33414a] dark:text-[#dfe6eb]">
                      {t('Contact.fields.message')}
                    </span>
                    <textarea
                      required
                      rows={6}
                      placeholder={t('Contact.fields.messagePlaceholder')}
                      value={formState.message}
                      onChange={(e) => setFormState((prev) => ({ ...prev, message: e.target.value }))}
                      className="w-full rounded-[1.5rem] border border-[#181A1D]/10 bg-[#f8f8f5] px-5 py-4 text-sm text-[#181A1D] outline-none transition-all duration-300 placeholder:text-[#8b98a2] focus:border-[#506C83] focus:bg-white dark:border-white/10 dark:bg-[#0d1419] dark:text-[#f5f7fa] dark:placeholder:text-[#6f808c] dark:focus:border-[#8ca3b5] dark:focus:bg-[#11191f]"
                    />
                  </label>

                  {formState.error ? (
                    <p className="rounded-[1.25rem] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200">
                      {formState.error}
                    </p>
                  ) : null}

                  <div className="flex flex-col gap-4 border-t border-[#181A1D]/10 pt-5 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
                    <p className="max-w-sm text-xs font-medium uppercase tracking-[0.18em] text-[#6d7a83] dark:text-[#8ea0ad]">
                      {t('Contact.assistance')}
                    </p>
                    <button
                      type="submit"
                      disabled={formState.loading}
                      className="group inline-flex h-11 items-center justify-center gap-1.5 self-start rounded-full bg-[#181A1D] px-4 py-2 text-[0.76rem] font-semibold tracking-[0.04em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#24313a] hover:shadow-[0_10px_22px_rgba(24,26,29,0.12)] disabled:cursor-not-allowed disabled:opacity-70 dark:bg-[#f5f7fa] dark:text-[#181A1D] dark:hover:bg-[#dfe6eb] sm:h-12 sm:px-4.5"
                    >
                      {formState.loading ? (
                        <Icon icon="tabler:loader-2" className="h-3 w-3 animate-spin" />
                      ) : (
                        <>
                          {t('Contact.fields.submit')}
                          <Icon
                            icon="tabler:arrow-up-right"
                            className="h-3 w-3 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                          />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-[minmax(0,1.18fr)_minmax(18rem,0.82fr)] lg:items-stretch">
        <div className="gsap-item rounded-[1.6rem] border border-black/5 bg-white/78 px-5 py-5 shadow-[0_14px_44px_rgba(24,26,29,0.06)] backdrop-blur-sm dark:border-white/10 dark:bg-white/5 dark:shadow-[0_14px_44px_rgba(0,0,0,0.2)] sm:px-6 sm:py-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-xl space-y-2.5">
              <p className="text-[0.68rem] font-semibold uppercase tracking-[0.26em] text-[#506C83] dark:text-[#c7d5e0]">
                {t('Contact.channelsEyebrow')}
              </p>
              <p className="max-w-xl text-[0.96rem] leading-7 text-[#2e3a43] dark:text-[#d7e0e7] sm:text-base">
                {t('Contact.channelsDescription')}
              </p>
            </div>
            <SocialIcons
              tooltipPosition="bottom"
              justifyContent="start"
              size="md"
              spacing="normal"
              className="flex-wrap py-0"
            />
          </div>
        </div>

        <div className="gsap-item rounded-[1.6rem] border border-black/5 bg-[#181A1D] px-5 py-5 text-white shadow-[0_14px_44px_rgba(24,26,29,0.14)] dark:border-white/10 dark:bg-[#121a20] sm:px-6 sm:py-6">
          <p className="text-[0.68rem] font-semibold uppercase tracking-[0.26em] text-[#b6cad7]">
            {t('Contact.bookingEyebrow')}
          </p>
          <h3 className="mt-2.5 text-[1.45rem] font-semibold tracking-[-0.04em] text-white sm:text-[1.6rem]">
            {t('Contact.bookingTitle')}
          </h3>
          <p className="mt-2.5 text-sm leading-6 text-[#d7e0e7]">
            {t('Contact.bookingDescription')}
          </p>
          <button
            data-cal-namespace="30min"
            data-cal-link="ellis-caicedo-hfdo7f/30min"
            data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}'
            className="mt-5 inline-flex min-h-11 items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-[#181A1D] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#dfe6eb]"
          >
            {t('Contact.bookCall')}
          </button>
        </div>
      </section>
    </article>
  );
}
